/**
 * Servicio de Onboarding para NFCero
 * Mantiene en BD el estado persistente y en localStorage
 * solo las banderas temporales necesarias para iniciar el tour.
 */
import axios from '@/services/axiosInstance'
import store from '@/store'

const STORAGE_KEYS = {
  START_ONBOARDING: 'nfcero_start_onboarding',
  CURRENT_STEP: 'nfcero_onboarding_step'
}

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')
  } catch (error) {
    return null
  }
}

const getCurrentUser = () => store.getters.currentUser || getStoredUser()

const OnboardingService = {
  /**
   * Verifica si el onboarding ya fue completado
   * @returns {boolean}
   */
  isOnboardingCompleted() {
    return Boolean(getCurrentUser()?.onboardingCompletedAt)
  },

  /**
   * Verifica si debe mostrar el onboarding (primera vez del usuario)
   * @returns {boolean}
   */
  shouldShowOnboarding() {
    return !this.isOnboardingCompleted()
  },

  /**
   * Verifica si el onboarding debe iniciarse (flag seteado después del login)
   * @returns {boolean}
   */
  shouldStartOnboarding() {
    return (
      localStorage.getItem(STORAGE_KEYS.START_ONBOARDING) === 'true' &&
      !this.isOnboardingCompleted()
    )
  },

  /**
   * Marca el inicio del onboarding (llamar después del login si es primera vez)
   */
  triggerOnboarding() {
    if (this.shouldShowOnboarding()) {
      localStorage.setItem(STORAGE_KEYS.START_ONBOARDING, 'true')
    }
  },

  /**
   * Limpia el flag de inicio del onboarding
   */
  clearOnboardingTrigger() {
    localStorage.removeItem(STORAGE_KEYS.START_ONBOARDING)
  },

  /**
   * Marca el onboarding como completado
   */
  async markOnboardingComplete() {
    try {
      const response = await axios.post('/auth/onboarding/complete')
      const onboardingCompletedAt = response.data?.user?.onboardingCompletedAt

      if (onboardingCompletedAt) {
        store.dispatch('updateUserProfile', { onboardingCompletedAt })
      }

      localStorage.removeItem(STORAGE_KEYS.START_ONBOARDING)
      localStorage.removeItem(STORAGE_KEYS.CURRENT_STEP)

      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al registrar el onboarding')
    }
  },

  /**
   * Reinicia el estado local del onboarding (útil para testing)
   */
  resetOnboarding() {
    localStorage.removeItem(STORAGE_KEYS.START_ONBOARDING)
    localStorage.removeItem(STORAGE_KEYS.CURRENT_STEP)
  },

  /**
   * Guarda el paso actual del onboarding
   * @param {string} step - Identificador del paso actual
   */
  setCurrentStep(step) {
    localStorage.setItem(STORAGE_KEYS.CURRENT_STEP, step)
  },

  /**
   * Obtiene el paso actual del onboarding
   * @returns {string|null}
   */
  getCurrentStep() {
    return localStorage.getItem(STORAGE_KEYS.CURRENT_STEP)
  }
}

export default OnboardingService

